import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { FormGroup, FormGroupDirective } from '@angular/forms';
import { Store } from '@ngxs/store';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { SpecLine } from '../../models/SpecLine';
import { AssetManagementService } from '../../services/asset-management.service';
import { SpecificationType } from '../../models/SpecLine';
import { AssetManagementState } from '../../../../shared/state/AssetManagementState';
import { Subject, takeUntil } from 'rxjs';
import { TranslationState } from '../../../../shared/state/TranslationState';
@Component({
  selector: 'app-spec-line-definition',
  templateUrl: './spec-line-definition.component.html',
  styleUrls: ['./spec-line-definition.component.scss'],
  standalone: false
})
export class SpecLineDefinitionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() specLine: SpecLine;
  @Input() editMode: boolean;
  protected translationService = inject(TranslationService);
  public specLineFormGroup = inject(FormGroupDirective);
  public store = inject(Store);
  public assetManagementService = inject(AssetManagementService);

  formGroup: FormGroup;
  applicationValues: string[];
  staticApplicationValues = Array.from(this.store.selectSnapshot(AssetManagementState.mrtSpecifications).keys());
  axleValues: string[];
  treadsValues: string[];
  makesValues: string[];
  seriesValues: string[];
  sizesValues: string[];
  viewMode: string;
  private readonly ngUnsubscribe = new Subject<void>();

  constructor() {}

  ngOnInit(): void {
    // remove this line when fixing temporory code
    this.applicationValues = this.translationService.translateList(
      this.compareStaticApplications(this.store.selectSnapshot(ProductsState.products).applications)
    );

    this.makesValues = this.translationService.translateList(this.store.selectSnapshot(ProductsState.products).makes);
    this.seriesValues = this.translationService.translateList(this.store.selectSnapshot(ProductsState.products).series);
    this.sizesValues = this.translationService.translateList(this.store.selectSnapshot(ProductsState.products).sizes);
    this.axleValues = this.translationService.translateList(this.store.selectSnapshot(ProductsState.products).axles);
    this.treadsValues = this.translationService.translateList(this.store.selectSnapshot(ProductsState.products).treads);
    this.viewMode = this.editMode ? 'edit-component' : 'view-component';

    this.formGroup = this.specLineFormGroup.control.get('definition') as FormGroup;
    if (this.specLine.type === 20) {
      this.formGroup.get('treads').clearValidators();
      this.formGroup.get('treads').updateValueAndValidity();
    }
    this.prepopulateForm();
    this.formGroup.get('applicationDefinition').valueChanges.subscribe((x) => {
      this.assetManagementService.setCurrentApplication(this.translationService.translate(x));
    });

    const parentFormGroup = this.specLineFormGroup.control as FormGroup;
    if (parentFormGroup && parentFormGroup.get('type')) {
      parentFormGroup
        .get('type')
        .valueChanges.pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((typeValue) => {
          this.specLine.type = typeValue;
          if (typeValue === SpecificationType.REPAIR_ONLY) {
            this.formGroup.get('treads').clearValidators();
            this.formGroup.get('treads').updateValueAndValidity();
          }
        });
    } else {
      console.error('Could not find type control in parent form');
    }

    this.store
      .select(TranslationState.currentLanguage)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.prepopulateForm();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.specLineFormGroup && this.formGroup) {
      this.formGroup = this.specLineFormGroup.control.get('definition') as FormGroup;
      if (this.specLine.type === 20) {
        this.formGroup.get('treads').clearValidators();
        this.formGroup.get('treads').updateValueAndValidity();
      }
      this.prepopulateForm();
    }

    if ('editMode' in changes && this.formGroup) {
      this.viewMode = changes.editMode.currentValue ? 'edit-component' : 'view-component';
      if (
        this.editMode &&
        !this.translationService
          .translateList(this.applicationValues)
          .includes(this.translationService.translate(this.specLine.applicationDefinition))
      ) {
        this.formGroup.patchValue({ applicationDefinition: '' });
      } else {
        this.formGroup.patchValue({ applicationDefinition: this.translationService.translate(this.specLine.applicationDefinition) });
      }
    }
  }
  prepopulateForm(): void {
    this.formGroup.patchValue({
      applicationDefinition: this.translationService.translate(this.specLine.applicationDefinition),
      axles: this.translationService.translateList(this.specLine.axles),
      sizes: this.translationService.translateList(this.specLine.sizes),
      makes: this.translationService.translateList(this.specLine.makes),
      treads: this.translationService.translateList(this.specLine.treads),
      series: this.translationService.translateList(this.specLine.series)
    });
  }

  // remove this method when fixing temporory code
  compareStaticApplications(productApplications: string[]): string[] {
    const matchedValues: string[] = [];
    matchedValues.push('All');
    this.staticApplicationValues.forEach((x) => {
      if (productApplications.includes(x)) {
        matchedValues.push(x);
      }
    });
    return matchedValues;
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  protected readonly SpecType = SpecificationType;
}
